/**
 *
 * @module playback
 * @desc playback sequence implementation
 * @see https://devflow.jenkins.us-east-1.bamgrid.net/job/SDK-Doc/job/spec-sdk/job/master/1766/artifact/dist/uml/architecture/scenarios/sequence-playback.svg
 *
 */

import Check from 'check-types';
import assert from 'assert';

import SDK from './../../sdk';
import BaseSequence from './../base';
import Exception from './../../services/exception/entry';

const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    assigned,
    boolean,
    object,
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @function getLaunchpadQaGraphQlQuery
 * @desc Basic Livewriter Test Stream 3 - VOD Type Playlist Playback - Web Media Test
 * @returns {SDK.Content.GraphQlQuery}
 *
 */
const getLaunchpadQaGraphQlQuery = () => {

    return new SDK.Content.GraphQlQuery(`{
        query(index: "launchpad_global", type: "Video") {
            meta {
                hits
            }
            hits {
                hit {
                    ... on Video {
                        type
                        contentId
                        partnerProgramId
                        epgPartnerProgramId
                        language
                        titles {
                            title
                        }
                        media {
                            mediaId
                            mediaState
                            playbackUrls {
                                rel
                                href
                            }
                        }
                    }
                }
            }
        }
    }`, '');

};

/**
 *
 * @class PlaybackSequence
 * @extends {BaseSequence}
 * @desc The primary SDK playback sequence. The playback sequence is a great choice for testing
 * complete end-to-end functionality. It interacts with the core features of the
 * SDK, i.e., Establish Anonymous Session, Establish Registered Session, Content Retrieval,
 * Media Session Establishment, and Playback Workflow.
 *
 * @example const playbackSequence = new SDK.Test.Sequences.Playback(
 *     config, Html5PlayerAdapter, videoElement, queryContext, graphQlQuery, geoProvider
 * );
 *
 */
export default class PlaybackSequence extends BaseSequence {

    /**
     *
     * @param {Object} [config] configuration used to build a BootstrapConfiguration instance
     * @param {SDK.Media.PlayerAdapter} PlayerAdapter player adapter to use in sequence
     * @param {NativePlayer} nativePlayer platform native player instance
     * @param {String} [queryContext='search'] GraphQl query context
     * @param {SDK.Content.GraphQlQuery} [graphQlQuery=getLaunchpadQaGraphQlQuery()] GraphQlQuery instance
     * @param {SDK.Token.GeoProvider} [geoProvider] GeoProvider instance to use
     * portion of the sequence should be executed
     * @param {String} [exceptionIdentifier=null] exception identifier,
     * i.e. 'blackout', 'stream-concurrency-violation', 'login.required', etc...
     * used for exception testing
     * @param {Boolean} [clearOnComplete=false] flag that determines if token refresh
     * should be cleared when the sequence is complete (useful when running tests)
     * @param {SDK.Services.Configuration.ConfigurationHostName} [configurationHostName=ConfigurationHostName.prod]
     * The configuration host name to use (dev or prod)
     * @throws {InvalidArgumentException}
     *
     */
    constructor(config, PlayerAdapter, nativePlayer, queryContext = 'search',
        graphQlQuery = getLaunchpadQaGraphQlQuery(), geoProvider,
        exceptionIdentifier = null, clearOnComplete = false) {

        const ctor = `PlaybackSequence(config, PlayerAdapter, nativePlayer, queryContext,
            graphQlQuery, geoProvider, exceptionIdentifier, clearOnComplete)`;

        const configError = `${ctor} config should be a plain Object`;
        const playerAdapterError = `${ctor} needs PlayerAdapter`;
        const nativePlayerError = `${ctor} needs NativePlayer instance`;
        const queryContextError = `${ctor} queryContext should be a String with length`;
        const graphQlQueryError = `${ctor} needs GraphQlQuery instance`;
        const geoProviderError = `${ctor} needs GeoProvider instance`;
        const exceptionIdentifierError = `${ctor} exceptionIdentifier should be a String with length`;
        const clearOnCompleteError = `${ctor} clearOnComplete should be a Boolean`;

        const GraphQlQuery = SDK.Content.GraphQlQuery;
        const GeoProvider = SDK.Token.GeoProvider;

        if (Check.assigned(config)) {
            object(config, configError, InvalidArgumentException);
        }

        assigned(PlayerAdapter, playerAdapterError, InvalidArgumentException);
        assigned(nativePlayer, nativePlayerError, InvalidArgumentException);
        nonEmptyString(queryContext, queryContextError, InvalidArgumentException);
        instanceStrict(graphQlQuery, GraphQlQuery, graphQlQueryError, InvalidArgumentException);

        if (Check.assigned(geoProvider)) {
            instanceStrict(geoProvider, GeoProvider, geoProviderError, InvalidArgumentException);
        }

        if (Check.assigned(exceptionIdentifier)) {
            nonEmptyString(exceptionIdentifier, exceptionIdentifierError, InvalidArgumentException);
        }

        boolean(clearOnComplete, clearOnCompleteError, InvalidArgumentException);

        super(config, geoProvider);

        /**
         *
         * @access public
         * @type {SDK.Media.PlayerAdapter}
         *
         */
        this.PlayerAdapter = PlayerAdapter;

        /**
         *
         * @access public
         * @type {NativePlayer}
         *
         */
        this.nativePlayer = nativePlayer;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.queryContext = queryContext;

        /**
         *
         * @access public
         * @type {SDK.Content.GraphQlQuery}
         *
         */
        this.graphQlQuery = graphQlQuery;

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.exceptionIdentifier = exceptionIdentifier;

        /**
         *
         * @access private
         * @type {Boolean}
         *
         */
        this.clearOnComplete = clearOnComplete;

    }

    /**
     *
     * @access public
     * @method execute
     * @desc runs the sequence to completion
     * @returns {Promise<SDK.Media.PlayerAdapter>}
     *
     * @example playbackSequence.execute().then(() => { console.log('success'); });
     *
     */
    execute() {

        const { config, PlayerAdapter, nativePlayer, queryContext, graphQlQuery } = this;
        const { geoProvider, clearOnComplete } = this;

        const Playlist = SDK.Media.Playlist;
        const ContentQuery = SDK.Content.ContentQuery;
        const BootstrapConfiguration = SDK.Services.Configuration.BootstrapConfiguration;
        const HTML5PlayerAdapter = SDK.Media.PlayerAdapter.Html5PlayerAdapter;

        return new Promise((resolve, reject) => {

            /** @type {SDK.Services.Configuration.BootstrapConfiguration} */
            const bootstrapConfiguration = new BootstrapConfiguration(config);

            /** @type {SDK.Content.GraphQlQuery} */
            const contentQuery = new ContentQuery(queryContext, graphQlQuery);

            /** @type {SDK.SdkSession} */
            let sdkSession;

            /** @type {SDK.Content.ContentApi} */
            let contentApi;

            /** @type {SDK.Media.MediaApi} */
            let mediaApi;

            /** @type {SDK.Media.MediaDescriptor} */
            let mediaDescriptor;

            /** @type {SDK.Media.MediaItem} */
            let mediaItem;

            /** @type {SDK.Media.PlaybackSession} */
            let playbackSession;

            /** @type {SDK.Media.PlayerAdapter} */
            let playerAdapter;

            /** @type {String} */
            let playbackUrl;

            /** 1 */
            SDK.SdkSession.createSdkSession(bootstrapConfiguration, geoProvider).then((sa) => {

                /** 2 */

                /** @type {SDK.SdkSession} sa */
                sdkSession = sa;

                /** @test */
                assert.ok(sa);

                /** Establish Anonymous Session */

                /** 3 */
                return sdkSession.initialize();

            }).then(() => {

                /** 4 */
                /** 5 */
                /** 7 */
                /** 8 */
                /** 9 */
                /** 10 */

                /** Repeat authorizeSessionWithAccountGrant(Account Grant Token) each time Account is updated */
                /** e.g. Entitlements change via IAP or purchase */

                /** Content Retrieval (BAMTECH CMS) */
                /** Alternatively, partner CMS is used */

                /** 11 */
                contentApi = sdkSession.contentApi;

                /** 12 */

                /** 13 */
                return contentApi.query({
                    query: contentQuery
                });

            }).then((contentData) => {

                /** Persisted GraphQL Query */
                /** 14 - JSON (including playbackUrl) */
                /** JSON typically deserialized to models */

                const data = contentData.data;
                let serializedData;

                try {
                    serializedData = JSON.stringify(data);
                } catch (error) {
                    serializedData = null;
                }

                /**
                 *
                 * @desc handle various payload structures without knowing the schema
                 * @note utility logic, outside of the official sequence
                 *
                 * 1. serialize the content data
                 * 2. grab the first valid href based on structure
                 *
                 */
                const hrefRegex = /(\b(https?):\/\/[-A-Z0-9+&@{#\/%?=~_|!:,.;]*[-A-Z0-9+&@}#\/%=~_|]\{scenario\})/ig; // eslint-disable-line
                const serializedDataMatch = serializedData.match(hrefRegex);
                playbackUrl = serializedDataMatch && serializedDataMatch.shift() || ''; // eslint-disable-line

                /** 15 */
                /** @type {SDK.Media.MediaDescriptor} */
                mediaDescriptor = new SDK.Media.MediaDescriptor({
                    mediaLocation: playbackUrl
                });

                /** 16 */
                mediaApi = sdkSession.mediaApi;

                return mediaApi.fetch(mediaDescriptor);

            }).then((_mediaItem) => {

                mediaItem = _mediaItem;

                /** @test {SDK.Media.MediaItem} */
                assert.ok(mediaItem.payload);
                assert.ok(mediaItem.descriptor);

                /** Playback Workflow */
                /** @type {SDK.Media.PlayerAdapter} */
                playerAdapter = new PlayerAdapter(nativePlayer);

                /** 17 */
                return mediaApi.createPlaybackSession(playerAdapter);

            }).then((ps) => {

                /** 18 */
                /** @type {SDK.Media.PlaybackSession} */
                playbackSession = ps;

                /** @test {SDK.Media.PlaybackSession} */
                assert.ok(playbackSession.playerAdapter);
                assert.ok(playbackSession.tokenManager);
                assert.ok(playbackSession.authCookieProvider);
                assert.ok(playbackSession.mediaManager);
                assert.ok(playbackSession.telemetryDispatcher);

                /** 19 */
                /** 20 */
                /** 21 */
                /** 22 */
                return mediaItem.getTrackingData();

            }).then(() => {

                /** 23 - Initialize Analytics */
                /** 24 - Initialize Advertising */

                /** 25 */
                return playbackSession.prepare(mediaItem);

            }).then((playlist) => {

                /** 25 */
                /** @test {SDK.Media.Playlist} */
                assert.ok(playlist instanceof Playlist);

                assert.ok(playlist.streamUri);
                assert.ok(playlist.playlistType);
                assert.ok(playlist.availablePlaylistTypes);

                /** @test {SDK.Media.PlayerAdapter} */
                assert.ok(playerAdapter.playlistUri);
                assert.ok(playerAdapter.nativePlayer);
                assert.ok(playerAdapter.listener);
                assert.ok(playerAdapter.accessToken);
                assert.ok(playerAdapter.boundHandlers);

                /** @desc rescan the source element on HTML5 platforms */
                if (playerAdapter instanceof HTML5PlayerAdapter) {
                    if (typeof nativePlayer.load === 'function') {
                        nativePlayer.load();
                    }
                }

                /** 26 */
                // nativePlayer.play();

                return Promise.resolve();

            }).then(() => {

                if (clearOnComplete) {
                    if (sdkSession.tokenManager) {
                        sdkSession.tokenManager.clearRetryRefresh();
                    }
                }

                /** Standard Player Interaction */

                return resolve(playerAdapter);

            }).catch((ex) => {

                return reject(ex);

            });

        });

    }

}



// WEBPACK FOOTER //
// ./src/sequences/media/playback.js